/* -----------------Helpers & Hooks--------------- */
import { useTranslation } from "react-i18next";
/* -----------------Types--------------- */
import type { SessionState } from "../../hooks/types";

import { events } from "@constants/tracking";
import { isMultiSelect } from "@features/session/helpers/session";
import { useAppStore } from "@hooks/useAppStore";
import type { DailyProgressState } from "@hooks/useDailyProgressStore";
import { getCurrentDate, getPlatform } from "@memorang/helpers/src/common";
import { calcScore } from "@memorang/helpers/src/scoring/multichoice";
import type { Confidence } from "@memorang/types/src/session/common";
import type {
	AIFeedbackResponse,
	AIResponsePayload,
	Choice,
	ItemResponseFormField,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { trackCustomEvent } from "analytics";
import { trackAnswerEvent } from "../../api/mutations";
import type { TrackAnswerEventRequest } from "../../api/types";
import { AI_SUBMITTABLE_VARIANTS, MAPPED_ITEM_TYPES } from "../../constants";
import { getNormalizedScore } from "../../helpers/ai-feedback";

const calculatePoints = ({
	itemType,
	totalPoints,
	itemPoints,
	isCorrect,
	answeredFormStates,
	aiFeedback,
}: {
	itemType?: OriginalItemTypes;
	totalPoints: number;
	itemPoints: number;
	isCorrect?: boolean;
	answeredFormStates?: Record<
		string,
		{
			response: string;
			isCorrect: boolean;
		}
	>;
	aiFeedback?: AIFeedbackResponse;
}) => {
	const baseFormula = (numPointsEarned: number) => {
		return Math.round((numPointsEarned / totalPoints) * itemPoints);
	};

	switch (itemType) {
		case "LEXICAL_CLOZE":
		case "MCQ":
		case "NOTICE_MCQ":
			return baseFormula(isCorrect ? 1 : 0);
		case "NOTE_COMPLETION": {
			const numEarnedPoints = Object.values(answeredFormStates || {}).reduce(
				(acc, curr) => {
					return acc + (curr.isCorrect ? 1 : 0);
				},
				0,
			);
			return baseFormula(numEarnedPoints);
		}
		case "STORY_TELLING":
		case "LONG_TURN":
		case "WRITING_EMAIL": {
			const numEarnedPoints = Object.values(
				aiFeedback?.subskillAssessments || {},
			).reduce((acc, curr) => {
				return acc + (curr.score ?? 0);
			}, 0);
			return baseFormula(numEarnedPoints);
		}
		default:
			return 0;
	}
};
const isSataAnswerCorrect = (
	choices: Choice[],
	selectedChoiceIds?: string[],
) => {
	const numRequiredAnswers = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;
	const numCorrectSelectedChoiceIds = selectedChoiceIds
		? selectedChoiceIds.filter((selectedChoiceId) => {
				const selectedChoice = choices.find(
					(item) => item.id === selectedChoiceId,
				);
				return selectedChoice ? selectedChoice.isCorrect : false;
			}).length
		: 0;
	const numCorrectChoiceIds = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;

	const isCorrect = numRequiredAnswers
		? numCorrectSelectedChoiceIds >= numRequiredAnswers &&
			selectedChoiceIds?.length === numCorrectSelectedChoiceIds
		: numCorrectChoiceIds === numCorrectSelectedChoiceIds;

	return isCorrect;
};

const getAnsweredFormStates = (
	fields: ItemResponseFormField[],
	currentFormStates?: Record<string, string>,
) => {
	return Object.fromEntries(
		Object.entries(currentFormStates || {}).map(([fieldId, fieldState]) => [
			fieldId,
			{
				response: fieldState,
				isCorrect:
					fieldState.toLocaleLowerCase() ===
					fields
						.find((field) => field.id === fieldId)
						?.expected_response?.toLocaleLowerCase(),
			},
		]),
	);
};
const isNoteCompletionAnswerCorrect = (
	fields: ItemResponseFormField[],
	currentFormStates?: Record<string, string>,
) => {
	const isCorrect = fields.every((field) => {
		const fieldState = currentFormStates?.[field.id] || "";
		return (
			fieldState?.toLocaleLowerCase() ===
			field.expected_response?.toLocaleLowerCase()
		);
	});
	return isCorrect;
};
const getShouldShowConfidencesSubmitButtons = (
	sessionState: SessionState,
	currentItemIndex: number,
) => {
	const {
		itemStates,
		answers,
		sessionItems,
		currentBlockIndex,
		isSummativeTest,
		formStates,
	} = sessionState;

	if (isSummativeTest) {
		return false;
	}
	const { id } = sessionItems[currentBlockIndex][currentItemIndex];

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { metadata } = currentItem;
	const itemType = metadata?.type;

	const currentItemState = itemStates?.get(id!);
	const currentAnswer = answers?.get(id!);

	const numMinChoicesRequired = currentItem.response?.numChoicesRequired?.min;

	const selectedChoiceIds = currentItemState?.selectedChoiceIds || [];

	const mappedItemType = MAPPED_ITEM_TYPES[itemType!];
	const getIsSelected = () => {
		switch (mappedItemType) {
			case "SATA":
				return (
					numMinChoicesRequired &&
					selectedChoiceIds?.length >= numMinChoicesRequired
				);
			case "NOTE_COMPLETION": {
				const requiredFieldIds =
					currentItem.response?.fields
						?.filter((field) => field.required)
						.map((field) => field.id) || [];
				const currentFormStates = formStates?.[id!];
				const isSelected = requiredFieldIds.every(
					(fieldId) => currentFormStates?.[fieldId],
				);
				return isSelected;
			}
			default:
				return selectedChoiceIds?.length > 0;
		}
	};
	const isSelected = getIsSelected();

	return isSelected && !currentAnswer;
};

const getNextButtonDisabled = ({
	mappedItemType,
	selectedChoiceIds = [],
}: {
	mappedItemType?: RenderableItemTypes;
	selectedChoiceIds: string[];
	minChoiceCount?: number;
	maxChoiceCount?: number;
}) => {
	if (mappedItemType !== "SATA" || !selectedChoiceIds.length) {
		return false;
	}

	// const isMinChoiceCountValid = !!(
	// 	minChoiceCount && selectedChoiceIds.length < minChoiceCount
	// );
	// const isMaxChoiceCountValid = !!(
	// 	maxChoiceCount && selectedChoiceIds.length < maxChoiceCount
	// );

	return false;
};
const getNextButtonStates = (
	sessionState: SessionState,
	currentItemIndex: number,
) => {
	const { t } = useTranslation();
	const { sessionItems, itemStates, inReviewMode, currentBlockIndex } =
		sessionState;

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { metadata, response, id } = currentItem;

	const type = metadata?.type;
	const minChoiceCount = response?.numChoicesRequired?.min;
	const maxChoiceCount = response?.numChoicesRequired?.max;
	const currentItemState = itemStates?.get(id!);

	const isSata = isMultiSelect({
		maxChoiceCount,
		minChoiceCount,
		mappedItemType: MAPPED_ITEM_TYPES[type!],
	});

	const selectedChoiceIds =
		currentItemState?.selectedChoiceIds ||
		currentItem.answer?.selectedChoiceIds ||
		[];

	const getIsSelected = () => {
		if (inReviewMode) {
			return true;
		}
		if (isSata) {
			return (
				selectedChoiceIds?.length >= (minChoiceCount || 0) ||
				currentItemState?.answered
			);
		}
		return (
			(selectedChoiceIds && selectedChoiceIds.length > 0) ||
			(currentOrders && currentOrders.length > 0) ||
			currentItemState?.answered
		);
	};

	const currentOrders = currentItemState?.currentOrders;

	const isSelected = getIsSelected();

	const disabled = getNextButtonDisabled({
		mappedItemType: MAPPED_ITEM_TYPES[type!],
		selectedChoiceIds: currentItemState?.selectedChoiceIds || [],
		minChoiceCount,
		maxChoiceCount,
	});
	return {
		text: isSelected
			? t("session.navigation.next")
			: t("session.navigation.skip"),
		showButtonNavSkip: !(isSelected || inReviewMode),
		disabled,
	};
};

const getShowButtonNavFinish = (
	{ answers, inReviewMode, currentBlockIndex, sessionItems }: SessionState,
	currentItemIndex: number,
) => {
	const currentBlock = sessionItems[currentBlockIndex];
	const currentItem = currentBlock[currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);
	const sessionLength = currentBlock.length;

	return (
		(currentAnswer != null || inReviewMode) &&
		currentItemIndex === sessionLength - 1
	);
};

const calculateAndUpdateProgress = (state: SessionState, forward?: boolean) => {
	const {
		updateProgress,
		currentProgress,
		numGoal,
		isSummativeTest,
		blockStates,
		currentBlockIndex,
	} = state;

	const finalProgress = isSummativeTest
		? blockStates?.get(currentBlockIndex)?.currentProgress || 0
		: currentProgress;

	let nextProgress = 0;

	if (forward) {
		const newProgress = finalProgress + 1 / numGoal!;
		nextProgress = newProgress;
		updateProgress(newProgress);
	} else {
		const newProgress = finalProgress - 1 / numGoal!;
		updateProgress(newProgress);
	}
	return nextProgress;
};

const getAccumulatedPoints = (
	choices: Choice[],
	selectedChoiceIds?: string[],
) => {
	const selectedChoicesWithPoints = choices.filter((item) => {
		return selectedChoiceIds?.includes(item.id) && item.points != null;
	});

	const accumulatedPoints = selectedChoicesWithPoints.length
		? selectedChoicesWithPoints.reduce(
				(acc, item) => acc + (item.points || 0),
				0,
			)
		: undefined;
	return accumulatedPoints;
};

const handlePostAnswer = (
	{
		state,
		startTime,
		confidence,
		dailyProgressState,
		projectId,
		organizationId,
		examId,
		mappedItemType,
		submitAnswer = true,
		aiFeedback,
		totalScore,
		slideshowDurationInSeconds,
		currentItemIndex,
	}: {
		state: SessionState;
		startTime: Date;
		confidence?: Confidence;
		dailyProgressState?: DailyProgressState;
		projectId: number;
		organizationId: number;
		examId?: string;
		mappedItemType?: RenderableItemTypes;
		submitAnswer?: boolean;
		aiFeedback?: AIFeedbackResponse;
		totalScore?: number;
		slideshowDurationInSeconds?: number;
		currentItemIndex: number;
	},
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
) => {
	const {
		sessionItems,
		itemStates,
		id: sessionId,
		updateAnswers,
		updateItemStates,
		updateAnswerEventInProgress,
		isRecommendedTask,
		updateItemAttempts,
		updateShowConfetti,
		currentBlockIndex,
		isSummativeTest,
		blockStates,
		collectionId,
		formStates,
		timeElapsed,
		sessionTime,
	} = state;

	const isImageChallengeApp = useAppStore.getState().app.isImageChallengeApp;

	const dailyProgressByData = dailyProgressState?.dailyProgressByData;
	const setDailyProgressByDate = dailyProgressState?.setDailyProgressByDate;

	const currentDate = getCurrentDate();
	const key = `${examId}-${currentDate}`;

	const currentDailyProgress = dailyProgressByData?.[key];

	const timeTaken = Math.min(
		Math.round((Date.now() - startTime.getTime()) / 1000),
		300,
	);

	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const { id: currentItemId, content, response } = currentItem;

	const itemPoints = response?.itemPoints ?? 0;
	const totalPoints = response?.totalPoints ?? 0;

	const choices = content?.choices ?? [];
	const maxChoiceCount = content?.maxChoiceCount;
	const minChoiceCount = content?.minChoiceCount;

	const currentItemState = itemStates?.get(currentItemId!);

	const answerId = choices?.find((item) => item.isCorrect)?.id;
	const correctChoiceIds = choices
		?.filter((item) => item.isCorrect)
		.map((item) => item.id);

	const numDistractors = choices ? choices.length - 1 : 0;

	const correctAnswerOrders = currentItem.response?.correctResponse?.map(
		(choice) => choice.id,
	);

	const isSequence = mappedItemType === "SEQUENCE";

	const selectedChoiceIds =
		currentItemState?.selectedChoiceIds ||
		currentItem.answer?.selectedChoiceIds ||
		[];

	const selectedChoiceItemId = selectedChoiceIds?.[0];
	const isSata = isMultiSelect({
		maxChoiceCount,
		minChoiceCount,
		mappedItemType: mappedItemType,
	});
	const isNoteCompletion = mappedItemType === "NOTE_COMPLETION";

	const currentFormStates = formStates?.[currentItemId!];

	const getIsCorrect = () => {
		if (isSequence) {
			return correctAnswerOrders?.every((order) =>
				currentItemState?.currentOrders?.includes(order),
			);
		}
		if (isSata) {
			return isSataAnswerCorrect(choices!, selectedChoiceIds);
		}
		if (isNoteCompletion) {
			return isNoteCompletionAnswerCorrect(
				currentItem.response?.fields || [],
				currentFormStates,
			);
		}
		return selectedChoiceItemId === answerId;
	};
	const isCorrect = getIsCorrect();

	const hints = currentItemState?.hints?.length
		? currentItemState?.hints
		: null;
	const currentBlockState = blockStates?.get(currentBlockIndex);
	const markedItemsForReview = currentBlockState?.markedItemsForReview;
	const highlightedItems = currentBlockState?.highlightedItems;

	const { score } = isSummativeTest
		? { score: isCorrect ? 100 : 0 }
		: calcScore(
				confidence ?? 0,
				Boolean(isCorrect),
				timeTaken,
				slideshowDurationInSeconds ?? 0,
				currentItem,
			);

	if (currentDailyProgress && isRecommendedTask && score > 90) {
		const { numCompleted, numGoal } = currentDailyProgress;
		const updatedProgress = {
			[key]: {
				numCompleted: (numCompleted || 0) + 1,
				numGoal,
				status: "ACTIVE" as const,
			},
		};
		setDailyProgressByDate?.(updatedProgress);
	}

	const itemType = currentItem.metadata?.type;
	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(itemType!);

	const answeredFormStates = getAnsweredFormStates(
		currentItem.response?.fields || [],
		currentFormStates,
	);
	const currentAnswer = {
		timeTaken,
		confidence: confidence ?? 0,
		correct: isCorrect,
		score,
		selectedChoiceIds,
		answeredFormStates,
	};

	if (isImageChallengeApp) {
		updateItemAttempts({
			itemId: `${currentItemId}`,
			answered: isCorrect,
			showTryAgain: !isCorrect,
			selectedChoiceId: selectedChoiceItemId,
			attempted: true,
			correct: isCorrect,
		});
		if (isCorrect) {
			updateAnswers(currentItemId!, currentAnswer);
			updateShowConfetti({ itemId: `${currentItemId}`, showConfetti: true });
		}
	} else if (!isSummativeTest) {
		updateAnswers(currentItemId!, currentAnswer);
		updateItemStates({
			itemId: currentItemId!,
			selectedChoiceId: selectedChoiceItemId!,
			correctAnswerIds: correctChoiceIds,
			scores: [score],
			answered: true,
		});
	}
	updateAnswerEventInProgress(true);

	if (!submitAnswer) {
		return;
	}

	const aiResponse: AIResponsePayload = {
		feedback: aiFeedback?.explanation ?? "",
		subskillAssessments:
			aiFeedback?.subskillAssessments?.map((subskill) => ({
				...subskill,
				maxScore: totalScore!,
			})) ?? [],
		ai_run_id: aiFeedback?.runDetails?.runId ?? "",
		ai_thread_id: aiFeedback?.runDetails?.threadId ?? "",
		metadata: {
			assistantId: aiFeedback?.runDetails?.assistantId ?? "",
		},
		answer: aiFeedback?.answer ?? "",
	};

	const aiNormalizedScore = getNormalizedScore(
		aiFeedback?.score ?? 0,
		totalScore!,
	);

	const explicitPoints = itemPoints
		? calculatePoints({
				itemType,
				totalPoints,
				itemPoints,
				isCorrect,
				aiFeedback,
				answeredFormStates,
			})
		: null;

	const accumulatedPoints = getAccumulatedPoints(choices, selectedChoiceIds);

	const trackAnswerEventRequest: TrackAnswerEventRequest = {
		itemId: currentItemId!,
		itemVersionId: currentItem.versionId || 1,
		sessionId,
		collectionId,
		projectId,
		organizationId,
		sessionType: isSummativeTest ? "SUMMATIVE" : "FORMATIVE",
		itemDetails: {
			type: "ANSWER_EVENT",
			itemType: itemType!,
			variant: itemType!,
		},
		response: {
			selectedChoiceIds: selectedChoiceIds ?? [],
			confidence: confidence ?? 0,
			chosenAnswerId: selectedChoiceItemId!,
			correctness: Boolean(isCorrect),
		},
		performance: {
			timeTaken,
			score: isAiSubmittable ? aiNormalizedScore : score || 0,
			xp: 0,
			points: explicitPoints ?? aiFeedback?.score ?? accumulatedPoints,
		},
		settings: {
			shuffled: false,
			numDistractors,
		},
		userInteraction: {
			marked: Boolean(
				currentItem.answer?.marked ||
					markedItemsForReview?.includes(currentItem.id!),
			),
			highlight:
				currentItem.answer?.highlight ||
				highlightedItems?.get(currentItem.id!) ||
				"",
			hints: hints || [],
			sessionTime: timeElapsed + (sessionTime || 0),
		},
		platform: getPlatform(),
		...(isAiSubmittable && aiResponse ? { aiResponse } : {}),
	};

	trackCustomEvent({
		eventName: events.sessionItemResponseSubmitted,
		confidence: confidence ?? 0,
		itemId: currentItemId,
		sessionId,
		timeTaken,
		type: "formative",
		correctness: isCorrect ? "true" : "false",
		selectedChoiceIds: selectedChoiceIds?.join(","),
		itemType: currentItem.metadata?.type,
		aiFeedback: JSON.stringify(aiFeedback),
	});
	// if (isMocked) {
	// 	updateAnswerEventInProgress(false);
	// 	return;
	// }

	trackAnswerEvent(trackAnswerEventRequest).then(() => {
		updateAnswerEventInProgress(false);
	});
};

type ShowAiSubmitResponseButtonParams = {
	sessionState: SessionState;
	currentItemIndex: number;
	currentUserResponse?: unknown;
	currentAudioResponse?: unknown;
	shouldValidateAudio?: boolean;
};

export const getShowSubmitAnswerAIFeedbackButton = ({
	sessionState,
	currentItemIndex,
	currentUserResponse,
	currentAudioResponse,
	shouldValidateAudio,
}: ShowAiSubmitResponseButtonParams) => {
	const { answers, sessionItems, currentBlockIndex } = sessionState;
	const currentItem = sessionItems[currentBlockIndex][currentItemIndex];
	const currentAnswer = answers?.get(currentItem.id!);

	const itemType = currentItem.metadata?.type;

	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(itemType!);

	const baseShowCondition = !currentAnswer && isAiSubmittable;

	const shouldShowSubmitAnswerAIFeedbackButton =
		(MAPPED_ITEM_TYPES[itemType!] === "SEQUENCE" && !currentAnswer) ||
		(baseShowCondition &&
			Boolean(currentUserResponse) &&
			(shouldValidateAudio ? Boolean(currentAudioResponse) : true));

	return {
		disabled: currentAnswer != null,
		show: shouldShowSubmitAnswerAIFeedbackButton,
	};
};

export {
	getShouldShowConfidencesSubmitButtons,
	getNextButtonStates,
	calculateAndUpdateProgress,
	getShowButtonNavFinish,
	handlePostAnswer,
};
